import React, { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import Chart from "react-apexcharts";
import ListGroupRowTitleValue from "../Common/ListGroupRowTitleValue";
import { useBalance } from "../../DataStore/BalanceContext";
import { numberFormat } from "../../util/araknerdlib";
import { useCurrency } from "../../util/SystemVariables";
import { Skeleton, SkeletonDonut, SkeletonPill } from "../../util/skeleton/Skeleton";
import FinanceCardHead from "./FinanceCardHead";
import SplitCashBankBar from "./Analytics/SplitCashBankBar";

const THEME = {
  indigo: "#4F46E5",
  slate: "#64748b",
};

const BalanceAccounts = () => {
  const {
    cashBankListing,
    cashBankBalance,
    loadingCashBank,
    loadingCashBankListing,
  } = useBalance();

  const currency = useCurrency();


  const { series, labels, total } = useMemo(() => {
    const cash = Number(cashBankBalance?.cash || 0);
    const bank = Number(cashBankBalance?.bank || 0);
    return { series: [cash, bank], labels: ["Cash", "Bank"], total: cash + bank };
  }, [cashBankBalance]);

  const options = useMemo(
    () => ({
      chart: { type: "donut", toolbar: { show: false } },
      labels,
      colors: [THEME.indigo, "rgba(79,70,229,.32)"],
      legend: {
        show: true,
        position: "right",
        offsetY: 8,
        fontSize: "13px",
        fontWeight: 800,
        labels: { colors: THEME.slate },
        markers: { width: 12, height: 12, radius: 12 },
        itemMargin: { vertical: 8 },
      },
      dataLabels: {
        enabled: true,
        formatter: (v) => `${v.toFixed(1)}%`,
        style: { fontSize: "12px", fontWeight: 900 },
        dropShadow: { enabled: false },
      },
      stroke: { width: 2, colors: ["#fff"] },
      tooltip: { y: { formatter: (val) => `${currency} ${numberFormat(val || 0)}` } },
      plotOptions: {
        pie: {
          donut: {
            size: "72%",
            labels: {
              show: true,
              name: {
                show: true,
                offsetY: -4,
                fontSize: "11px",
                fontWeight: 900,
                color: "rgba(71,85,105,.9)",
              },
              value: {
                show: true,
                offsetY: 6,
                fontSize: "18px",
                fontWeight: 950,
                color: "#0F172A",
                formatter: () =>
                  `${Math.round((series[1] / Math.max(total, 1)) * 100)}%`,
              },
              total: { show: false },
            },
          },
        },
      },
      noData: { text: "No balances" },
    }),
    [labels, currency, series, total]
  );

  const isLoadingChart = (loadingCashBank ?? false) || cashBankBalance == null;
  const isLoadingList =
    (loadingCashBankListing ?? false) || !Array.isArray(cashBankListing);

  /* --------------- Skeleton --------------- */
  if (isLoadingChart && isLoadingList) {
    return (
      <div className="yx-card mb-3">
        <div className="yx-card-head d-flex justify-content-between align-items-center">
          <Skeleton w="140px" h={14} />
          <Skeleton w="120px" h={34} />
        </div>

        <div className="yx-card-body">
          <div className="row no-gutters align-items-center">
            <div className="col-12 col-lg-7 yx-bcDonut">
              <SkeletonDonut size={210} />
            </div>

            <div className="col-12 col-lg-5">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="yx-bcRowSkel">
                  <div style={{ flex: 1 }}>
                    <Skeleton w="180px" h={14} />
                    <Skeleton w="260px" h={11} style={{ marginTop: 6 }} />
                  </div>
                  <SkeletonPill w={130} h={32} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <FinanceCardHead
      title="Bank & Cash"
      right={
        <Link to="/asset-contra-new" className="rac-action">
          New Contra
        </Link>
      }
      value={total}
      currency={currency}
      subtitle="Across cash + bank accounts"
      formatValue={(v) => `${currency} ${numberFormat(v)}`}
    >
      {/* Donut + accounts */}
      <div className="row no-gutters align-items-start">
        {/* Donut */}
         {/* <div className="yx-bcDonutSection">
          <div className="yx-bcDonutWrap2">
            <Chart
              options={options}
              series={total > 0 ? series : [1, 1]}
              type="donut"
              height={220}
            />
          </div>
        </div> */}
        <SplitCashBankBar
          bank={cashBankBalance?.bank}
          cash={cashBankBalance?.cash}
          currency={currency}
          // compact
        />

        <div className="yx-divider" />
        <div className="yx-bcListFull overflow-auto" style={{width:'100%', maxHeight: 120}}>
        {Array.isArray(cashBankListing) && cashBankListing.length > 0 ? (
          <ul className="list-group yx-bcListGroup">
            {cashBankListing.map((item, idx) => {
              const bal = Number(item.balance) || 0;
              const isBank = String(item.account_type || "").toLowerCase() === "bank";

              return (
                <ListGroupRowTitleValue
                  key={idx}
                  heading={item.account_name}
                  subheading={`Account No: ${item.account_number}`}
                  value={`${currency} ${numberFormat(bal)}`}
                  valueClass={isBank ? "yx-pill yx-pill--indigo" : "yx-pill yx-pill--soft"}
                />
              );
            })}
          </ul>
        ) : (
          <div className="text-muted small">No Bank or Cash accounts found.</div>
        )}
      </div>
      </div>
    </FinanceCardHead>
  );
};

export default BalanceAccounts;
